import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import WorldDbHeader from './WorldDbHeader';
import { useActiveWorld } from '../hooks/useActiveWorld';
import { CreditGateProvider } from '../hooks/useCreditGate';
// Global voice-capture engine — mounted once here so ANY child component
// (Cockpit Vault, Ultimate Setup, future surfaces) can call
// useSpeakEngine().openCapture({ preset, onResult }) without wiring its own modal.
import { SpeakEngineProvider } from '../speakEngine';
import './WorldLayout.css';

const WorldLayout = () => {
  const location = useLocation();
  const { userId, currentUserId, canWrite, features, accountType, hasFeature, isLoading } = useActiveWorld();
  const canEdit = typeof canWrite === 'function' ? canWrite() : true;
  
  const isTeamMemberWithLimitedAccess = accountType === 'member' && !features.includes('all');
  const hasLeadsOnlyAccess = isTeamMemberWithLimitedAccess && 
    features.includes('leads') && 
    !features.includes('inbox') && 
    !features.includes('offers') && 
    !features.includes('settings');
  
  const getActiveView = () => {
    if (location.pathname === '/leads') return 'leads';
    if (location.pathname === '/leadswipe') return 'leadswipe';
    if (location.pathname === '/tasks') return 'tasks';
    if (location.pathname.startsWith('/offers/')) return 'offerCreator';
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('view') || 'tableOne';
  };

  const [activeView, setActiveView] = useState(getActiveView());

  useEffect(() => {
    setActiveView(getActiveView());
  }, [location]);

  // Only window scroll counts: Inbox / Settings scroll inside fixed-height
  // panes, and hiding the header there would leave an empty 85px strip.
  useEffect(() => {
    const body = document.body;
    let lastY = window.scrollY;
    let hidden = false;
    let frame = null;

    const setHidden = (next) => {
      if (next === hidden) return;
      hidden = next;
      body.classList.toggle('world-nav-hidden', next);
    };

    const update = () => {
      frame = null;
      const y = Math.max(window.scrollY, 0);
      const delta = y - lastY;
      if (y < 85) setHidden(false);
      else if (delta > 6) setHidden(true);
      else if (delta < -6) setHidden(false);
      if (Math.abs(delta) > 6 || y < 85) lastY = y;
    };

    const onScroll = () => {
      if (frame == null) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame != null) cancelAnimationFrame(frame);
      body.classList.remove('world-nav-hidden');
    };
  }, [location.pathname, location.search]);

  if (!isLoading && hasLeadsOnlyAccess && location.pathname !== '/leadswipe') {
    return <Navigate to="/leadswipe" replace />;
  }

  const isFlowsPage = location.pathname === '/leadswipe';

  return (
    // Sits at the layout level so the "out of credits" popup and the shared
    // balance survive navigation between Targets, Leads and Flows — mounting it
    // per page would re-trigger the popup on every route change.
    <CreditGateProvider userId={userId}>
      <SpeakEngineProvider userId={userId} currentUserId={currentUserId} canEdit={canEdit}>
        <div className="worldDbContainer" style={isFlowsPage ? { marginTop: 0 } : undefined}>
          {!isFlowsPage && (
            <WorldDbHeader 
              activeView={activeView}
              setActiveView={setActiveView}
              isLeadsOnlyAccess={hasLeadsOnlyAccess}
            />
          )}
          <Outlet context={{ activeView, setActiveView }} />
        </div>
      </SpeakEngineProvider>
    </CreditGateProvider>
  );
};

export default WorldLayout;
